import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BalanceEntryHistory } from "@/components/BalanceEntryHistory";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d) => {
  if (!d) return "-";
  const [y, m, day] = d.slice(0, 10).split("-");
  return day ? `${day}/${m}/${y}` : d;
};
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "-");

const Field = ({ label, value, testId, mono }) => (
  <div className="space-y-0.5">
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`text-sm font-medium break-words ${mono ? "font-mono" : ""}`} data-testid={testId}>{value || "-"}</p>
  </div>
);

export const BalanceEntryViewDialog = ({ entry, onOpenChange }) => {
  const items = entry?.items || [];
  const itemsTotal = items.reduce((s, it) => s + (it.total_value || 0), 0);
  return (
    <Dialog open={!!entry} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="balance-entry-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            NF {entry?.nf_number || "sem numero"}
            <Badge variant={entry?.source === "nf" ? "default" : "secondary"} className="text-xs">
              {entry?.source === "nf" ? "NF Importada" : "Manual"}
            </Badge>
          </DialogTitle>
          <DialogDescription>Visualizacao somente leitura do lancamento.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Fornecedor" value={entry?.supplier} testId="balance-view-supplier" />
            <Field label="CNPJ" value={entry?.supplier_cnpj} testId="balance-view-cnpj" mono />
            <Field label="Numero da NF" value={entry?.nf_number} testId="balance-view-nf-number" mono />
            <Field label="Data de Emissao" value={fmtDate(entry?.nf_date)} testId="balance-view-nf-date" />
          </div>

          <div className="border border-border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descricao do Produto</TableHead>
                  <TableHead className="text-right">Qtd</TableHead>
                  <TableHead className="text-right">Vl. Unit.</TableHead>
                  <TableHead className="text-right">IPI</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">Sem itens</TableCell></TableRow>
                ) : items.map((it, idx) => (
                  <TableRow key={idx} data-testid={`balance-view-item-${idx}`}>
                    <TableCell className="text-sm">{it.description}</TableCell>
                    <TableCell className="text-sm text-right">{(it.quantity || 0).toLocaleString("pt-BR")}</TableCell>
                    <TableCell className="text-sm text-right">{fmtBRL(it.unit_value)}</TableCell>
                    <TableCell className="text-sm text-right">{fmtBRL(it.ipi)}</TableCell>
                    <TableCell className="text-sm text-right font-semibold">{fmtBRL(it.total_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 rounded-lg bg-muted/40 p-4">
            <Field label="Soma dos Itens" value={fmtBRL(itemsTotal)} testId="balance-view-items-total" />
            <Field label="Frete" value={fmtBRL(entry?.freight)} testId="balance-view-freight" />
            <Field label="Desconto" value={fmtBRL(entry?.discount)} testId="balance-view-discount" />
            <Field label="ICMS ST" value={fmtBRL(entry?.icms_st)} testId="balance-view-icmsst" />
            <div className="space-y-0.5">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Valor Total da NF</p>
              <p className="font-heading text-xl font-black tracking-tighter" data-testid="balance-view-total">{fmtBRL(entry?.total_value)}</p>
            </div>
          </div>

          {entry?.notes && <Field label="Observacoes" value={entry.notes} testId="balance-view-notes" />}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-muted-foreground">
            <Field label="Lancado por" value={entry?.created_by} testId="balance-view-created-by" />
            <Field label="Lancado em" value={fmtDateTime(entry?.created_at)} testId="balance-view-created-at" />
            <Field label="Ultima alteracao" value={fmtDateTime(entry?.updated_at)} testId="balance-view-updated-at" />
          </div>

          <BalanceEntryHistory entryId={entry?.id} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="balance-view-close-btn">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
